import React, { useEffect, useState, useRef } from 'react';
import ButtonScogoClosedOutlined from './Buttons/ButtonScogoClosedOutlined';
import ButtonScogoPrimary from './Buttons/ButtonScogoPrimary';
import { textCss } from './Css';
import IconToolTip from './IconToolTip';
import Popover from './Popover';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '../utils/common';

const AdvanceSearchBox = ({
    onCancel,
    placeholder,
    allowAdvancedSearch,
    advancedSearchFields = [],
    inputWidth = 'w-full md:w-96',
    inputWidthOnFocus = 'w-full md:w-608',
    showClosedIcon = true,
    isExpandingEnabled = true,
    autoFocus = true,
    inputClass = '',
    className = '',
    ticketSearchAs = [],
    defaultPage = 1,
    onChange = () => { },
    searchKey = ''
}) => {
    const [advancedSearchParams, setAdvancedSearchParams] = useState();
    const [prevAdvancedSearchParams, setPrevAdvancedSearchParams] = useState();
    const [isFocus, setIsFocus] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [matchedFields, setMatchedFields] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const query = useQuery();

    const iconContainerRef = useRef();
    const popOverRef = useRef();

    useEffect(() => {
        let s = '';
        if (searchKey) {
            s = searchKey;
        }
        if (ticketSearchAs && ticketSearchAs.length > 0) {
            ticketSearchAs.forEach(({ key }) => {
                let queryValue = query.get(key);
                if (queryValue) s = queryValue;
            })
        }
        setSearchTerm(s);
    }, [searchKey, ticketSearchAs, query])

    useEffect(() => {
        let advSearch = {};
        if (advancedSearchFields?.length > 0) {
            advancedSearchFields.forEach(({ key }) => {
                const queryValue = query.get(key);
                if (queryValue) advSearch = { ...advSearch, [key]: queryValue };
            });
        };

        if (Object.keys(advSearch).length > 0) {
            setAdvancedSearchParams(advSearch);
        } else {
            setAdvancedSearchParams({});
        }
    }, [advancedSearchFields, query]);

    const isAdvancedSearchActive = advancedSearchParams && Object.keys(advancedSearchParams).length > 0;

    const advancedSearchIcon = (
        <IconToolTip placement={'bottom'} title={`${isAdvancedSearchActive ? 'Advanced Search Applied' : 'Advanced Search'}`}>
            <span className={`material-icons ${isAdvancedSearchActive ? 'text-scogoorange' : ''} hover:text-scogoorange cursor-pointer text-font16`} onClick={() => setIsFocus(true)}>
                tune
            </span>
        </IconToolTip>
    );

    const handleAdvancedSearch = () => {
        Object.entries(advancedSearchParams || {}).forEach(([key, value]) => {
            setSearchParams(params => {
                params.set(key, value);
                return params;
            });
        });
        popOverRef.current.close();
    };

    const isSearchInputEmpty = !searchTerm || searchTerm.length === 0;

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && highlightedIndex >= 0) {
            const selected = matchedFields[highlightedIndex];
            if (selected) {
                setSearchParams((params) => {
                    const fieldsToRemove = ticketSearchAs.map(({ key }) => key);
                    fieldsToRemove.forEach((key) => params.delete(key));
                    params.set(selected.key, searchTerm);
                    params.set('page', defaultPage);
                    params.delete('query');
                    return params;
                });
                setMatchedFields([]);
                setHighlightedIndex(-1);
            }
        } else if (e.key === 'Enter') {
            onChange(e, { removeTheseKeys: ticketSearchAs.map(({ key }) => key) });
            setMatchedFields([]);
        } else if (matchedFields.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlightedIndex((prev) => (prev + 1) % matchedFields.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightedIndex((prev) => (prev - 1 + matchedFields.length) % matchedFields.length);
            }
        };
    };

    useEffect(() => {
        // Default-select the first "search as" suggestion so pressing Enter applies it without arrowing down.
        setHighlightedIndex(matchedFields.length > 0 ? 0 : -1);
    }, [searchTerm, matchedFields]);

    const onClear = () => {
        setSearchTerm('');
        onCancel();
        setSearchParams(params => {
            const fieldsToRemove = ticketSearchAs.map(({ key }) => key);
            fieldsToRemove.forEach((key) => {
                params.delete(key)
            });
            return params;
        });
        setAdvancedSearchParams();
        setPrevAdvancedSearchParams();
        setMatchedFields([]);
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (value?.length > 0) {
            setMatchedFields(ticketSearchAs);
        } else {
            onClear();
        }
    };

    const advancedSearch = (
        <Popover trigger={advancedSearchIcon} ref={popOverRef} containerRef={iconContainerRef} onClose={() => setIsFocus(false)}>
            <div className={'w-full md:w-608 max-w-full'}>
                <div style={{ height: '3rem' }} className="flex justify-between items-center px-4 py-3 border-b border-scogoddd">
                    <h3 className="text-lg font-medium text-gray-900">Advanced Search</h3>
                    {isAdvancedSearchActive ? <IconToolTip title="Clear Filters">
                        <span
                            className="text-2xl material-icons text-gray-500 hover:text-scogoorange cursor-pointer"
                            onClick={() => {
                                setSearchParams(params => {
                                    const fieldsToRemove = advancedSearchFields.map(({ key }) => key);
                                    fieldsToRemove.forEach((key) => {
                                        params.delete(key)
                                    });
                                    return params;
                                });
                                setAdvancedSearchParams();
                            }}
                        >
                            filter_alt_off
                        </span>
                    </IconToolTip> : null}
                </div>

                {advancedSearchFields.map((field) => (
                    <div className={`px-4 py-2`} key={field.label}>
                        <label className='block border-b-2 border-scogoddd'>
                            <p className={`${textCss?.inputLabel}`}>{field.label}</p>
                            <input
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleAdvancedSearch();
                                    }
                                }}
                                style={{ height: '3.1rem' }}
                                type='text'
                                className={`${textCss?.inputText} block w-full border-none focus:ring-0`}
                                value={advancedSearchParams?.[field.key] || ''}
                                onChange={(e) =>
                                    setAdvancedSearchParams((prev) => ({
                                        ...prev,
                                        [field.key]: e.target.value.trimStart()
                                    }))
                                }
                            />
                        </label>
                    </div>
                ))}

                <div className="flex justify-end gap-3 px-4 py-4 border-t border-scogoddd">
                    <ButtonScogoClosedOutlined
                        buttonClass='px-6 py-2'
                        textOrComponent={'Cancel'}
                        onClick={() => {
                            popOverRef.current.close();
                        }}
                    />
                    <ButtonScogoPrimary
                        buttonClass='px-6 py-2'
                        textOrComponent='Search'
                        onClick={() => {
                            handleAdvancedSearch();
                        }}
                    />
                </div>
            </div>
        </Popover>
    );

    // The "search as" field reflected by the badge: the highlighted suggestion while typing (defaults to the first item),
    // otherwise the field currently applied via the query params.
    const highlightedField = matchedFields.length > 0 && highlightedIndex >= 0 ? matchedFields[highlightedIndex] : null;
    const activeSearchAsField = ticketSearchAs.find(({ key }) => query.get(key));
    const selectedSearchAsField = highlightedField || activeSearchAsField;
    const showSearchAsLabel = !isSearchInputEmpty && Boolean(selectedSearchAsField);

    return (
        <div className={`text-gray-400 focus-within:text-gray-600 relative flex items-center ${className}`}>
            <div className="absolute left-0 flex items-center justify-center ml-2 md:ml-4">
                <img className={'h-5 w-5'} src={process.env.PUBLIC_URL + '/img/search.png'} alt='Search' />
            </div>

            <input
                value={searchTerm}
                onChange={handleInputChange}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                autoFocus={autoFocus}
                className={`text-xl md:text-scogo15 rounded-md py-3 pl-10 md:pl-12 focus:outline-none w-full border border-scogoccc focus:border-scogoorange focus:ring-0 items-center ${(isFocus || !isSearchInputEmpty) && isExpandingEnabled ? inputWidthOnFocus : inputWidth
                    } transition-all duration-300 ${showSearchAsLabel ? 'pr-[150px]' : 'pr-12 md:pr-16'} ${inputClass}`}
                onFocus={() => setIsFocus(true)}
                onBlur={() => setIsFocus(false)}
            />

            {matchedFields.length > 0 && (
                <div className="absolute top-full mt-2 w-full bg-white text-gray-900 rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
                    <div className="px-4 py-2 text-sm text-gray-500 uppercase tracking-wide font-medium">
                        Search Ticket As
                    </div>
                    {matchedFields.map(({ key, label }, index) => (
                        <div
                            onClick={() => {
                                setSearchParams(params => {
                                    const fieldsToRemove = ticketSearchAs.map(({ key }) => key);
                                    fieldsToRemove.forEach((key) => {
                                        params.delete(key)
                                    });
                                    params.set(key, searchTerm);
                                    params.set('page', defaultPage);
                                    params.delete('query');
                                    return params;
                                });
                                setMatchedFields([]);
                            }}
                            key={key}
                            className={`${highlightedIndex === index ? 'bg-pink-100' : 'hover:bg-gray-100'} flex items-center px-4 py-3 text-md hover:bg-pink-100 cursor-pointer transition-colors duration-150 gap-4`}
                        >
                            <div className="pt-1 truncate max-w-[50%] font-medium text-gray-800">
                                {searchTerm}
                            </div>
                            <span className={`max-w-max ml-2 bg-gray-200 text-gray-800 text-[10px] px-2 py-0.5 rounded-md font-mono`}>
                                as {label}
                            </span>
                        </div>
                    ))}

                </div>
            )}

            <div className='absolute flex items-center right-0 h-full pr-2 md:pr-3 gap-2' ref={iconContainerRef}>
                {showSearchAsLabel && (
                    <span
                        title={`Searching as ${selectedSearchAsField.label}`}
                        className='shrink-0 max-w-[100px] truncate bg-gray-200 text-gray-800 text-[10px] px-2 py-0.5 rounded-md font-mono whitespace-nowrap'
                    >
                        as {selectedSearchAsField.label}
                    </span>
                )}
                {allowAdvancedSearch && advancedSearch}
                {!isSearchInputEmpty && showClosedIcon ? (
                    <IconToolTip placement={'bottom'} title={`Clear`}>
                        <span
                            className='material-icons hover:text-scogoorange cursor-pointer text-font16 ml-1'
                            onClick={onClear}
                        >
                            close
                        </span>
                    </IconToolTip>
                ) : null}
            </div>
        </div>
    );
};

export default AdvanceSearchBox;
